import React from 'react'
import { Position } from '@xyflow/react'
import type { ProjectLabel } from '@bandos/shared'
import type { LabelContextType } from '../../../../context/label/LabelContextDef'
import { NodeLabels } from '../Base/components/NodeLabels'
import { StoryboardNodeHeader } from './StoryboardHeader'
import { StoryboardPanelGrid } from './StoryboardPanelGrid'
import {
  StoryboardWrapper,
  StoryboardNodeContainer,
  StoryboardTargetHandle,
} from '../styles/StoryboardNodeStyles'

interface StoryboardFullViewProps {
  nodeId: string
  zoomScale: number
  displayTitle: string
  isReadOnly: boolean
  labelsEnabled: boolean
  storyboardUrls: string[]
  frameCaptions: string[] | undefined
  panelErrors: boolean[]
  onPanelError: (index: number) => void
  labels: ProjectLabel[] | undefined
  mindmapId: string | undefined
  labelContext: LabelContextType
  onClickNode: () => void
  onDeleteNode: (nodeId: string) => void
  onDownloadPNG: () => Promise<void>
  onCopyToClipboard: () => Promise<void>
  onSelectLabel: (labelId: string) => Promise<void>
  onCreateLabel: (labelText: string, color: string) => Promise<void>
  onRemoveLabel: (labelId: string) => Promise<void>
  onDeleteLabel: (labelId: string) => Promise<void>
}

export const StoryboardFullView = React.memo(
  ({
    nodeId,
    zoomScale,
    displayTitle,
    isReadOnly,
    labelsEnabled,
    storyboardUrls,
    frameCaptions,
    panelErrors,
    onPanelError,
    labels,
    mindmapId,
    labelContext,
    onClickNode,
    onDeleteNode,
    onDownloadPNG,
    onCopyToClipboard,
    onSelectLabel,
    onCreateLabel,
    onRemoveLabel,
    onDeleteLabel,
  }: StoryboardFullViewProps) => {
    return (
      <StoryboardWrapper $zoomScale={zoomScale}>
        <StoryboardNodeContainer onClick={onClickNode}>
          <StoryboardTargetHandle
            type="target"
            position={Position.Left}
            id="target"
            isConnectableStart={false}
          />
          <StoryboardNodeHeader
            nodeId={nodeId}
            zoomScale={zoomScale}
            displayTitle={displayTitle}
            onDeleteNode={onDeleteNode}
            onDownloadPNG={onDownloadPNG}
            onCopyToClipboard={onCopyToClipboard}
          />
          <StoryboardPanelGrid
            storyboardUrls={storyboardUrls}
            frameCaptions={frameCaptions}
            panelErrors={panelErrors}
            onPanelError={onPanelError}
          />
        </StoryboardNodeContainer>
        {labelsEnabled && (
          <NodeLabels
            labels={labels}
            nodeId={nodeId}
            mindmapId={mindmapId}
            isReadOnly={isReadOnly}
            labelContext={labelContext}
            onSelectLabel={onSelectLabel}
            onCreateLabel={onCreateLabel}
            onRemoveLabel={onRemoveLabel}
            onDeleteLabel={onDeleteLabel}
          />
        )}
      </StoryboardWrapper>
    )
  }
)

StoryboardFullView.displayName = 'StoryboardFullView'
